import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MessageCircle, ArrowRight, User, Check, CheckCheck, Clock,
  RefreshCw, XCircle, Search, Archive, RotateCcw, Home, Settings, LogOut, FileCheck2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_TRIANGLE = '/brand/ast-logo-asb606.png';

export default function SupportAudit() {
  const navigate = useNavigate();
  const { user: currentUser, getAuthHeaders, logout } = useAuth();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const [conversations, setConversations] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef(null);
  const headers = getAuthHeaders();

  const fetchArchived = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/whatsapp/audit/conversations`, { headers });
      setConversations(res.data.data || []);
    } catch (err) {
      console.log('Audit fetch:', err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (phone) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/whatsapp/audit/messages/${phone}`, { headers });
      setMessages(res.data.data || []);
    } catch (err) {
      console.log('Audit messages fetch:', err.message);
    }
  };

  useEffect(() => {
    fetchArchived();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedContact) fetchMessages(selectedContact.phone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContact]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const restore = async (phone) => {
    try {
      await axios.post(`${BACKEND_URL}/api/whatsapp/audit/${phone}/restore`, null, { headers });
      toast.success(isAr ? 'تمت إعادة المحادثة إلى مركز الدعم' : 'Conversation restored');
      setSelectedContact(null);
      setMessages([]);
      fetchArchived();
    } catch {
      toast.error(isAr ? 'فشل إعادة المحادثة' : 'Restore failed');
    }
  };

  const filtered = conversations.filter((c) =>
    !searchQuery ||
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.phone?.includes(searchQuery)
  );

  const formatTime = (ts) => ts ? new Date(ts).toLocaleTimeString(isAr ? 'ar-SA' : 'en-US', { hour: '2-digit', minute: '2-digit' }) : '';
  const formatDate = (ts) => {
    if (!ts) return '';
    const date = new Date(ts);
    const today = new Date();
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return isAr ? 'اليوم' : 'Today';
    if (date.toDateString() === yesterday.toDateString()) return isAr ? 'أمس' : 'Yesterday';
    return date.toLocaleDateString(isAr ? 'ar-SA' : 'en-US', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="min-h-screen bg-[#030303]" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-amber-500/5 rounded-full blur-[150px]" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#050505]/80 backdrop-blur-2xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/dashboard?platform=support')}
                className="w-10 h-10 bg-white/[0.03] rounded-xl flex items-center justify-center hover:bg-[#d4af37]/10 transition-all border border-white/10 hover:border-[#d4af37]/40"
                data-testid="audit-back-btn"
              >
                <ArrowRight className={`h-5 w-5 text-[#d4af37] ${isAr ? '' : 'rotate-180'}`} />
              </motion.button>

              <div className="flex items-center gap-3">
                <motion.img
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  src={LOGO_TRIANGLE}
                  alt="ASB606"
                  className="w-10 h-10 object-contain drop-shadow-[0_0_15px_rgba(212,175,55,0.4)]"
                />
                <div>
                  <h1 className="text-lg font-bold text-white flex items-center gap-2">
                    <FileCheck2 className="h-5 w-5 text-amber-400" />
                    {isAr ? 'تدقيق الدعم' : 'Support Audit'}
                  </h1>
                  <p className="text-xs text-[#71717A]">
                    {isAr ? 'أرشيف المحادثات المُغلقة (قراءة فقط)' : 'Archive of closed conversations (read-only)'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                onClick={fetchArchived}
                variant="outline"
                className="border-white/10 text-[#A1A1AA] hover:text-white hover:bg-white/5"
                data-testid="audit-refresh-btn"
              >
                <RefreshCw className="h-4 w-4 me-2" />
                {isAr ? 'تحديث' : 'Refresh'}
              </Button>
              <LanguageToggle />
              <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => navigate('/dashboard')} className="w-10 h-10 bg-white/[0.03] rounded-xl flex items-center justify-center hover:bg-[#d4af37]/10 transition-all border border-white/10 hover:border-[#d4af37]/40">
                <Home className="h-5 w-5 text-[#A1A1AA]" />
              </motion.button>
              <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => navigate('/account-settings')} className="w-10 h-10 bg-white/[0.03] rounded-xl flex items-center justify-center hover:bg-[#d4af37]/10 transition-all border border-white/10 hover:border-[#d4af37]/40">
                <Settings className="h-5 w-5 text-[#A1A1AA]" />
              </motion.button>
              <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={logout} className="w-10 h-10 bg-red-500/10 rounded-xl flex items-center justify-center hover:bg-red-500/20 transition-all border border-red-500/20">
                <LogOut className="h-5 w-5 text-red-400" />
              </motion.button>
              <div className="flex items-center gap-3 ps-3 border-s border-white/10">
                <div className="text-end hidden sm:block">
                  <p className="text-sm font-medium text-[#F5F5F5]">{currentUser?.name}</p>
                  <p className="text-xs text-[#71717A]">{isAr ? 'مدير' : 'Admin'}</p>
                </div>
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#d4af37] to-[#c9a227] flex items-center justify-center text-black font-bold text-sm">
                  {currentUser?.name?.charAt(0) || 'A'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <p className="text-sm text-[#71717A]">
          {isAr ? 'لوحة التحكم / الدعم والمساعدة / تدقيق الدعم' : 'Dashboard / Support / Support Audit'}
        </p>
      </div>

      <main className="relative z-10 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden h-[calc(100vh-200px)] flex">
            {/* Sidebar list */}
            <div className="w-[380px] border-e border-white/10 flex flex-col">
              <div className="p-4 border-b border-white/10">
                <div className="relative">
                  <Search className={`absolute ${isAr ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A]`} />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={isAr ? 'بحث في الأرشيف...' : 'Search archive...'}
                    className={`bg-white/5 border-white/10 text-white placeholder:text-[#71717A] ${isAr ? 'pr-10' : 'pl-10'} rounded-xl`}
                    data-testid="audit-search-input"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="p-4 space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-20 bg-white/[0.02] rounded-xl animate-pulse" />
                    ))}
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="p-8 text-center text-[#808080]">
                    <Archive className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>{isAr ? 'لا توجد محادثات مُدقّقة' : 'No archived conversations'}</p>
                    <p className="text-sm mt-1">
                      {isAr ? 'المحادثات المُرسلة للتدقيق تظهر هنا' : 'Sent-to-audit chats appear here'}
                    </p>
                  </div>
                ) : (
                  filtered.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setSelectedContact(c)}
                      className={`w-full p-4 flex items-start gap-3 hover:bg-white/[0.03] transition-colors ${isAr ? 'text-right' : 'text-left'} border-b border-white/5 ${
                        selectedContact?.phone === c.phone ? 'bg-white/[0.04]' : ''
                      }`}
                      data-testid={`audit-contact-${c.phone}`}
                    >
                      <div className="w-12 h-12 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                        <User className="h-6 w-6 text-amber-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-semibold text-white truncate">{c.name}</p>
                          <span className="text-[10px] text-[#808080]">{formatDate(c.archived_at)}</span>
                        </div>
                        <p className="text-xs text-[#71717A] truncate mb-1" dir="ltr">+{c.phone}</p>
                        <p className="text-sm text-[#808080] truncate mb-2">{c.last_message}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-amber-300 bg-amber-500/15 border border-amber-500/30">
                            <FileCheck2 className="h-3 w-3" />
                            {isAr ? 'مُدقّقة' : 'Audited'}
                          </span>
                          {c.status_at_archive && (
                            <span className="text-[10px] text-[#606060]">
                              {c.status_at_archive === 'resolved' ? (isAr ? 'تم الحل' : 'Resolved') : (isAr ? 'مغلقة' : 'Closed')}
                            </span>
                          )}
                          {c.message_count > 0 && (
                            <span className="text-[10px] text-[#606060]">
                              · {c.message_count} {isAr ? 'رسالة' : 'msgs'}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Reader panel */}
            <div className="flex-1 flex flex-col">
              {selectedContact ? (
                <>
                  <div className="p-4 border-b border-white/10 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                        <User className="h-5 w-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="font-semibold text-white">{selectedContact.name}</p>
                        <p className="text-xs text-[#808080]" dir="ltr">+{selectedContact.phone}</p>
                      </div>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-amber-300 bg-amber-500/15 border border-amber-500/30">
                        <FileCheck2 className="h-3 w-3" />
                        {isAr ? `مُدقّقة بواسطة ${selectedContact.archived_by_name || selectedContact.archived_by || '—'}` : `Archived by ${selectedContact.archived_by_name || selectedContact.archived_by || '—'}`}
                      </span>
                    </div>

                    <Button
                      size="sm"
                      onClick={() => restore(selectedContact.phone)}
                      className="bg-sky-600 hover:bg-sky-700 text-white"
                      data-testid="audit-restore-btn"
                    >
                      <RotateCcw className="h-4 w-4 me-1" />
                      {isAr ? 'إعادة إلى الدعم' : 'Restore to Support'}
                    </Button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {messages.map((msg) => {
                      let TickIcon = null;
                      let tickClass = '';
                      let tickTitle = '';
                      if (msg.direction === 'outgoing') {
                        const ds = msg.delivery_status || msg.status;
                        if (ds === 'failed' || msg.status === 'failed') { TickIcon = XCircle; tickClass = 'text-red-300'; tickTitle = isAr ? 'فشل التسليم' : 'Failed'; }
                        else if (ds === 'read') { TickIcon = CheckCheck; tickClass = 'text-sky-200'; tickTitle = isAr ? 'مقروءة' : 'Read'; }
                        else if (ds === 'delivered') { TickIcon = CheckCheck; tickClass = 'text-white/60'; tickTitle = isAr ? 'سُلّمت' : 'Delivered'; }
                        else if (ds === 'sent') { TickIcon = Check; tickClass = 'text-white/60'; tickTitle = isAr ? 'مُرسلة' : 'Sent'; }
                        else { TickIcon = Clock; tickClass = 'text-white/60'; tickTitle = isAr ? 'قيد الإرسال' : 'Pending'; }
                      }
                      return (
                        <div
                          key={msg.id}
                          className={`flex ${
                            msg.direction === 'outgoing' ? (isAr ? 'justify-start' : 'justify-end') :
                            msg.direction === 'system' ? 'justify-center' :
                            (isAr ? 'justify-end' : 'justify-start')
                          }`}
                        >
                          {msg.direction === 'system' ? (
                            <div className="bg-white/[0.04] text-[#808080] text-xs px-4 py-2 rounded-full">
                              {msg.text}
                            </div>
                          ) : (
                            <div
                              className={`max-w-[70%] p-3 rounded-2xl ${
                                msg.direction === 'outgoing'
                                  ? (msg.status === 'failed' || msg.delivery_status === 'failed'
                                      ? 'bg-red-500/25 border border-red-500/40 text-white'
                                      : 'bg-[#25D366]/90 text-white')
                                  : 'bg-white/[0.05] text-white border border-white/5'
                              }`}
                            >
                              <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                              <div className="flex items-center justify-end gap-1 mt-1">
                                <span className="text-[10px] opacity-70">{formatTime(msg.created_at)}</span>
                                {msg.direction === 'outgoing' && TickIcon && (
                                  <span title={tickTitle} className={`inline-flex ${tickClass}`}>
                                    <TickIcon className="h-3.5 w-3.5" />
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="p-3 border-t border-white/10 bg-white/[0.02] text-center">
                    <p className="text-xs text-[#71717A] flex items-center justify-center gap-2">
                      <Archive className="h-3 w-3" />
                      {isAr ? 'هذه المحادثة في وضع التدقيق — لا يمكن الرد فيها' : 'This conversation is under audit — replies disabled'}
                    </p>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-[#808080]">
                  <div className="text-center">
                    <FileCheck2 className="h-20 w-20 mx-auto mb-4 opacity-20 text-amber-400" />
                    <p className="text-xl font-medium">{isAr ? 'تدقيق الدعم' : 'Support Audit'}</p>
                    <p className="text-sm mt-2 max-w-sm mx-auto">
                      {isAr ? 'اختر محادثة من الأرشيف لمراجعتها' : 'Select an archived conversation to review'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
